import { Box, styled, Typography } from '@mui/material';
import Row from '@components/layout-util-components/row';
import { RunReportModel } from '@store/reports/reports-slice';
import { getColorForId } from '@util/colour-identifiers';
import { shouldShrinkReportView } from '@store/reports/reports-selectors';
import { useAppSelector } from '@store/store';
import { Gap } from '@components/layout-util-components/gap';
import { ReportDataSplit } from '@hooks/report/use-report-data-split-hook';

interface Props {
  model: RunReportModel;
  splitData: ReportDataSplit;
  causeBoxCount: number;
  totalCoverage: number;
  onSelectSegment: (rowId: number) => void;
}

export const CoverageStatContainer = styled('div')<{ isShrunk?: boolean }>(
  ({ isShrunk }) => ({
    border: '1px solid rgba(0,0,0,0.25)',
    borderRadius: 5,
    padding: '5px 10px',
    display: 'inline-flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    alignItems: 'center',
    gap: 1,
    width: 120,

    '.value': {
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '21px',
      letterSpacing: '0px',
      textAlign: 'center',
    },

    '.title': {
      fontSize: '13px',
      fontWeight: '500',
      lineHeight: '17px',
      letterSpacing: '0.15000000596046448px',
      textAlign: 'center',
      textTransform: 'uppercase',
      color: '#949FA2 !important',
    },

    ...(isShrunk && {
      border: '1px solid #D8DBE3',
      borderRadius: '5px',
      background: '#F8F8FA',
      padding: '12px',
      flexDirection: 'row',
      gap: 3,

      '.value,.title': {
        fontSize: '11px',
        fontWeight: '500',
        lineHeight: '17px',
        letterSpacing: '0.15000000596046448px',
        textAlign: 'left',
        textTransform: 'uppercase',
      },

      '.title': {
        fontWeight: '400 !important',
      },
    }),
  })
);

const Container = styled('div')<{ isShrunk?: boolean }>({
  border: '1px solid #D8DBE3',
  background: '#F8F8FA',
  padding: 20,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'row',

  '.coverage-bar-container': {
    display: 'flex',
    width: '100%',
    backgroundColor: '#E9E9E9',
    height: 24,
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: 10,
  },
  '.stat-container': {
    border: '1px solid #D8DBE3',
    background: '#fff',
    padding: 10,
    borderRadius: 5,
    textAlign: 'center',
    minWidth: '125px',
    '.value': {
      color: '#000',
      fontSize: 15,
    },
    '.title': {
      color: '#868F91',
      fontSize: 10,
      textTransform: 'uppercase',
      fontWeight: '500',
    },
  },
});

const BarSegment = styled('div')<{ percent: number; color: string }>(
  ({ percent, color }) => ({
    cursor: 'pointer',
    display: 'inline-block',
    height: 24,
    backgroundColor: color,
    width: `${percent}%`,
  })
);

export default function CoverageOverviewContainer({
  model,
  splitData,
  causeBoxCount,
  totalCoverage,
  onSelectSegment,
}: Props) {
  const isShrunk = useAppSelector(shouldShrinkReportView);

  return (
    <Container isShrunk={isShrunk}>
      <Box flexGrow={1}>
        <Typography variant="body2">
          {model.reportSubTypeName} coverage
        </Typography>
        <div className="coverage-bar-container">
          {splitData.allRows.map((row) => {
            return (
              <BarSegment
                key={row.clientGeneratedId}
                percent={splitData.percentages[row.clientGeneratedId] ?? 0.0}
                color={row.colour ?? getColorForId(row.clientGeneratedId)}
                onClick={() => onSelectSegment(row.clientGeneratedId)}
              />
            );
          })}
        </div>
        {isShrunk && (
          <>
            <Gap size={10} />
            <Row sx={{ alignItems: 'stretch', marginTop: '15px' }}>
              <CoverageStatContainer>
                <Typography className="value">{causeBoxCount}</Typography>
                <Typography className="title">Cause boxes</Typography>
              </CoverageStatContainer>
              <Gap size={10} />
              <CoverageStatContainer>
                <Typography className="value">
                  {totalCoverage.toFixed(1)}%
                </Typography>
                <Typography className="title">
                  {model.reportSubTypeName} COVERAGE
                </Typography>
              </CoverageStatContainer>
            </Row>
          </>
        )}
      </Box>
      {!isShrunk && (
        <>
          <Gap size={32} />
          <Row>
            <div className="stat-container">
              <Typography className="value">{causeBoxCount}</Typography>
              <Typography className="title">Cause boxes</Typography>
            </div>
            <Gap size={10} />
            <div className="stat-container">
              <Typography className="value">{totalCoverage}%</Typography>
              <Typography className="title">
                {model.reportSubTypeName} COVERAGE
              </Typography>
            </div>
          </Row>
        </>
      )}
    </Container>
  );
}
